/**
 * v2 SectionHeader — page-level title row.
 *
 * Layout: display-serif title (h1) · optional sans subtitle · right-aligned
 * action slot for buttons. Replaces v1 page-internal h1 patterns.
 *
 * Used at the top of every v2 page below the topbar.
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface SectionHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned action slot — typically a row of buttons. */
  actions?: ReactNode;
  className?: string;
  /** Override heading level. Defaults to h1 for top-of-page; pass 2 for sub-sections. */
  level?: 1 | 2 | 3;
}

export function SectionHeader({
  title,
  subtitle,
  actions,
  className,
  level = 1,
}: SectionHeaderProps) {
  const Heading: keyof JSX.IntrinsicElements = (`h${level}` as const);
  const titleSize = level === 1 ? "text-[24px]" : level === 2 ? "text-[18px]" : "text-[15px]";
  return (
    <div className={cn("flex items-end justify-between gap-4", className)}>
      <div className="min-w-0">
        <Heading
          className={cn(
            "font-display leading-tight text-foreground",
            titleSize
          )}
        >
          {title}
        </Heading>
        {subtitle !== undefined && (
          <p className="mt-1 text-[12.5px] text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {actions !== undefined && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </div>
  );
}
